// v2/pages/admin/AdminCustomerDetailV2.js — Yonetim / Musteri Detayi
// URL: /v2/admin/customers/:kod?tab=users|moves|orders|logins
import React, { useCallback, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Store, Mail, Wallet, Receipt, ShoppingCart, Users, UserPlus,
  Eye, EyeOff, Pencil, Trash2, Power, PowerOff, Copy, Check, X, RotateCw,
  Search, ChevronLeft, ChevronRight, ShieldAlert, AlertTriangle, Inbox,
  Loader2, ArrowDownLeft, ArrowUpRight, Globe, Clock, KeyRound,
} from 'lucide-react';
import { useAdminCustomer, useAdminTransactions, adminUserApi } from '../../hooks/useAdminCustomer';
import UserFormModal from '../../components/admin/UserFormModal';
import ToastStack from '../../components/Toast';
import useToast from '../../hooks/useToast';
import { formatTRY, formatTRYShort, formatNumber } from '../../utils/format';
import { zamanTR, basHarfler, SIPARIS_DURUM } from '../../utils/adminFormat';
import { SkelPanel } from '../../components/Skeleton';
import s from '../../styles/AdminCustomerDetailV2.module.css';
import '../../styles/tokens.css';

const SEKMELER = [
  { id: 'users', label: 'Kullanıcılar', ikon: Users },
  { id: 'moves', label: 'Cari Hareketler', ikon: Receipt },
  { id: 'orders', label: 'Siparişler', ikon: ShoppingCart },
  { id: 'logins', label: 'Girişler', ikon: Globe },
];
const HAREKET_TIPI = [
  { id: '', label: 'Tümü' },
  { id: 'debit', label: 'Borç' },
  { id: 'credit', label: 'Alacak' },
];

const tarihTR = (ymd) => {
  if (!ymd) return '-';
  const [y, a, g] = String(ymd).split('-');
  return y ? `${g}.${a}.${y}` : '-';
};

/** Parolayi gosterip kopyalayan kucuk bilesen */
function ParolaHucresi({ kullanici, onHata }) {
  const [parola, setParola] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [kopyalandi, setKopyalandi] = useState(false);

  const getir = useCallback(async () => {
    if (parola !== null) { setParola(null); return; }
    setYukleniyor(true);
    try {
      const v = await adminUserApi.parola(kullanici.id);
      if (!v || !v.success) throw new Error((v && v.message) || 'Parola alınamadı');
      setParola(v.password || '');
    } catch (e) {
      onHata((e && e.message) || 'Parola alınamadı');
    } finally {
      setYukleniyor(false);
    }
  }, [parola, kullanici.id, onHata]);

  const kopyala = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(parola || '');
      setKopyalandi(true);
      setTimeout(() => setKopyalandi(false), 1600);
    } catch (e) {
      onHata('Kopyalanamadı');
    }
  }, [parola, onHata]);

  return (
    <span className={s.pwCell}>
      <button
        type="button" className={s.pwBtn} onClick={getir} disabled={yukleniyor}
        title={parola === null ? 'Parolayı göster' : 'Gizle'}
      >
        {yukleniyor ? <Loader2 size={14} className={s.spin} />
          : parola === null ? <Eye size={14} /> : <EyeOff size={14} />}
        <span className={s.pwText}>
          {parola === null ? 'Parola' : (parola || '(boş)')}
        </span>
      </button>
      {parola !== null && parola && (
        <button type="button" className={s.copyBtn} onClick={kopyala} title="Kopyala">
          {kopyalandi ? <Check size={14} className={s.okIcon} /> : <Copy size={14} />}
        </button>
      )}
    </span>
  );
}

function Onay({ metin, onEvet, onHayir, bekliyor }) {
  return (
    <div className={s.confirm}>
      <AlertTriangle size={16} />
      <span className={s.confirmText}>{metin}</span>
      <button type="button" className={s.confirmNo} onClick={onHayir} disabled={bekliyor}>Vazgeç</button>
      <button type="button" className={s.confirmYes} onClick={onEvet} disabled={bekliyor}>
        {bekliyor ? <Loader2 size={14} className={s.spin} /> : null} Evet, sil
      </button>
    </div>
  );
}

export default function AdminCustomerDetailV2() {
  const { kod } = useParams();
  const [sp, setSp] = useSearchParams();
  const sekme = sp.get('tab') || 'users';
  const { items: toastlar, push, dismiss } = useToast();

  const { data, loading, error, yenile } = useAdminCustomer(kod);
  const [modalAcik, setModalAcik] = useState(false);
  const [duzenlenen, setDuzenlenen] = useState(null);
  const [silinecek, setSilinecek] = useState(null);
  const [islemde, setIslemde] = useState(false);

  // hareketler sekmesi
  const [hq, setHq] = useState('');
  const [htip, setHtip] = useState('');
  const [hsayfa, setHsayfa] = useState(1);
  const hParams = useMemo(() => ({ q: hq, type: htip, page: String(hsayfa), per: '20' }),
    [hq, htip, hsayfa]);
  const { data: hareket, loading: hYuk, error: hHata } =
    useAdminTransactions(kod, hParams, sekme === 'moves');

  const sekmeSec = (id) => setSp((o) => {
    const p = new URLSearchParams(o);
    if (id === 'users') p.delete('tab'); else p.set('tab', id);
    return p;
  }, { replace: true });

  const kaydedildi = useCallback((mesaj) => {
    setModalAcik(false);
    setDuzenlenen(null);
    push(mesaj, 'success');
    yenile();
  }, [push, yenile]);

  const durumDegistir = useCallback(async (k) => {
    setIslemde(true);
    try {
      const v = await adminUserApi.guncelle(k.id, { durum: !k.active });
      if (!v || !v.success) throw new Error((v && v.message) || 'İşlem başarısız');
      push(k.active ? 'Kullanıcı pasife alındı' : 'Kullanıcı aktifleştirildi', 'success');
      yenile();
    } catch (e) {
      push((e && e.message) || 'İşlem başarısız', 'error');
    } finally {
      setIslemde(false);
    }
  }, [push, yenile]);

  const sil = useCallback(async () => {
    if (!silinecek) return;
    setIslemde(true);
    try {
      const v = await adminUserApi.sil(silinecek.id);
      if (!v || !v.success) throw new Error((v && v.message) || 'Silinemedi');
      push('Kullanıcı silindi', 'success');
      setSilinecek(null);
      yenile();
    } catch (e) {
      push((e && e.message) || 'Silinemedi', 'error');
    } finally {
      setIslemde(false);
    }
  }, [silinecek, push, yenile]);

  const c = (data && data.customer) || {};
  const kullanicilar = (data && data.users) || [];
  const siparisler = (data && data.recent_orders) || [];
  const girisler = (data && data.recent_logins) || [];
  const kisit = data && data.restriction;
  const ilk = loading && !data;

  if (error && !data) {
    return (
      <div className={s.page}>
        <Link to="/v2/admin/customers" className={s.back}><ArrowLeft size={15} /> Müşteriler</Link>
        <div className={s.errorBox}>
          <AlertTriangle size={26} />
          <p className={s.errorTitle}>Müşteri bilgisi alınamadı</p>
          <p className={s.errorText}>{error}</p>
          <button type="button" className={s.primaryBtn} onClick={yenile}>
            <RotateCw size={15} /> Tekrar dene
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={s.page}>
      <Link to="/v2/admin/customers" className={s.back}><ArrowLeft size={15} /> Müşteriler</Link>

      <div className={s.hero}>
        <span className={s.avatar}>{basHarfler(c.title || kod)}</span>
        <div className={s.heroText}>
          <h2 className={s.title}>{ilk ? 'Yükleniyor…' : (c.title || kod)}</h2>
          <div className={s.meta}>
            <span className={s.metaCode}><Store size={13} /> {kod}</span>
            {c.group && <span className={s.metaItem}>Grup {c.group}</span>}
            {c.email && <span className={s.metaItem}><Mail size={13} /> {c.email}</span>}
            {c.first_transaction && (
              <span className={s.metaItem}><Clock size={13} /> İlk hareket {tarihTR(c.first_transaction)}</span>
            )}
          </div>
        </div>
        <button type="button" className={s.ghostBtn} onClick={yenile} disabled={loading}>
          <RotateCw size={15} className={loading ? s.spin : ''} />
          <span className={s.btnText}>Yenile</span>
        </button>
      </div>

      {kisit && kisit.active && (
        <div className={s.banner}>
          <ShieldAlert size={20} />
          <div className={s.bannerBody}>
            <p className={s.bannerTitle}>Bu müşteride borç kısıtlaması aktif</p>
            <p className={s.bannerText}>
              {kisit.since ? `${tarihTR(String(kisit.since).slice(0, 10))} tarihinden beri` : 'Tarih yok'}
              {kisit.due ? ` · Son ödeme ${tarihTR(String(kisit.due).slice(0, 10))}` : ''}
              {kisit.min_debt_days ? ` · ${kisit.min_debt_days} gün ve üzeri borç` : ''}
            </p>
          </div>
        </div>
      )}

      <div className={s.stats}>
        <div className={s.stat}>
          <span className={`${s.statIcon} ${c.balance > 0 ? s.tRed : s.tGreen}`}><Wallet size={17} /></span>
          <span className={s.statBody}>
            <span className={`${s.statValue} ${c.balance > 0 ? s.debt : ''}`}>{formatTRY(c.balance)}</span>
            <span className={s.statLabel}>Bakiye · {formatNumber(c.transactions)} hareket</span>
          </span>
        </div>
        <div className={s.stat}>
          <span className={`${s.statIcon} ${s.tBlue}`}><Receipt size={17} /></span>
          <span className={s.statBody}>
            <span className={s.statValue}>{formatTRYShort(c.revenue)}</span>
            <span className={s.statLabel}>Toplam ciro (KDV hariç)</span>
          </span>
        </div>
        <div className={s.stat}>
          <span className={`${s.statIcon} ${s.tViolet}`}><ShoppingCart size={17} /></span>
          <span className={s.statBody}>
            <span className={s.statValue}>{formatNumber(c.orders)}</span>
            <span className={s.statLabel}>
              Sipariş satırı{c.last_order ? ` · son ${zamanTR(c.last_order)}` : ''}
            </span>
          </span>
        </div>
        <div className={s.stat}>
          <span className={`${s.statIcon} ${s.tAmber}`}><Inbox size={17} /></span>
          <span className={s.statBody}>
            <span className={s.statValue}>{formatTRYShort(c.pending_amount)}</span>
            <span className={s.statLabel}>Sepette · {formatNumber(c.pending_lines)} kalem</span>
          </span>
        </div>
      </div>

      <div className={s.tabs}>
        {SEKMELER.map((t) => {
          const Ikon = t.ikon;
          const sayi = t.id === 'users' ? kullanicilar.length
            : t.id === 'orders' ? siparisler.length
              : t.id === 'logins' ? (data && data.login_count) : null;
          return (
            <button
              key={t.id} type="button"
              className={`${s.tab} ${sekme === t.id ? s.tabOn : ''}`}
              onClick={() => sekmeSec(t.id)}
            >
              <Ikon size={15} /> {t.label}
              {sayi !== null && sayi !== undefined && <span className={s.tabCount}>{formatNumber(sayi)}</span>}
            </button>
          );
        })}
      </div>

      {sekme === 'users' && (
        <section className={s.panel}>
          <div className={s.panelHead}>
            <h3 className={s.panelTitle}><Users size={17} /> Kullanıcılar</h3>
            <button
              type="button" className={s.primaryBtn}
              onClick={() => { setDuzenlenen(null); setModalAcik(true); }}
            >
              <UserPlus size={15} /> <span className={s.btnText}>Kullanıcı Ekle</span>
            </button>
          </div>
          <div className={s.panelBody}>
            {ilk ? <SkelPanel rows={2} head={false} />
              : kullanicilar.length === 0 ? (
                <div className={s.empty}>
                  <Users size={22} />
                  <p className={s.emptyTitle}>Bu müşterinin kullanıcısı yok</p>
                  <p className={s.emptyText}>Portale giriş yapabilmesi için kullanıcı ekleyin.</p>
                </div>
              ) : (
                <ul className={s.userList}>
                  {kullanicilar.map((u) => (
                    <li key={u.id} className={`${s.userCard} ${u.active ? '' : s.userOff}`}>
                      <span className={s.userAv}>{basHarfler(u.ad_soyad)}</span>
                      <div className={s.userMain}>
                        <div className={s.userTop}>
                          <span className={s.userName}>{u.ad_soyad}</span>
                          {u.is_admin && <span className={`${s.pill} ${s.pillNavy}`}>Yönetici</span>}
                          <span className={`${s.pill} ${u.active ? s.pillGreen : s.pillGray}`}>
                            {u.active ? 'Aktif' : 'Pasif'}
                          </span>
                          {u.online && <span className={`${s.pill} ${s.pillBlue}`}>Oturum açık</span>}
                        </div>
                        <div className={s.userMeta}>
                          <span><Mail size={12} /> {u.mail}</span>
                          {u.phone && <span>{u.phone}</span>}
                          {u.gorev && <span>{u.gorev}</span>}
                          {u.ext && <span>Dahili {u.ext}</span>}
                        </div>
                        {!u.is_admin && (
                          <div className={s.userPw}>
                            <KeyRound size={12} className={s.pwIcon} />
                            <ParolaHucresi kullanici={u} onHata={(m) => push(m, 'error')} />
                          </div>
                        )}
                      </div>
                      <div className={s.userActions}>
                        {!u.is_admin && (<>
                          <button
                            type="button" className={s.actBtn} title="Düzenle"
                            onClick={() => { setDuzenlenen(u); setModalAcik(true); }}
                          ><Pencil size={15} /></button>
                          <button
                            type="button" className={s.actBtn} disabled={islemde}
                            title={u.active ? 'Pasife al' : 'Aktifleştir'}
                            onClick={() => durumDegistir(u)}
                          >{u.active ? <PowerOff size={15} /> : <Power size={15} />}</button>
                          </>)}
                          <button
                            type="button" className={`${s.actBtn} ${s.actDanger}`} title="Sil"
                            onClick={() => setSilinecek(u)}
                          ><Trash2 size={15} /></button>
                        </div>
                      {silinecek && silinecek.id === u.id && (
                        <div className={s.confirmWrap}>
                          <Onay
                            metin={`${u.ad_soyad} kalıcı olarak silinecek. Bu işlem geri alınamaz.`}
                            onEvet={sil} onHayir={() => setSilinecek(null)} bekliyor={islemde}
                          />
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
          </div>
        </section>
      )}

      {sekme === 'moves' && (
        <section className={s.panel}>
          <div className={s.panelHead}>
            <h3 className={s.panelTitle}><Receipt size={17} /> Cari Hareketler</h3>
            {hareket && (
              <div className={s.panelMeta}>
                <span>Borç <b>{formatTRYShort(hareket.summary.debit)}</b></span>
                <span>Alacak <b>{formatTRYShort(hareket.summary.credit)}</b></span>
                <span>Bakiye <b>{formatTRYShort(hareket.summary.balance)}</b></span>
              </div>
            )}
          </div>
          <div className={s.moveTools}>
            <div className={s.searchBox}>
              <Search size={15} className={s.searchIcon} />
              <input
                className={s.search} type="search" placeholder="Açıklama veya fiş no…"
                value={hq} onChange={(e) => { setHq(e.target.value); setHsayfa(1); }}
              />
              {hq && (
                <button type="button" className={s.clearBtn} onClick={() => { setHq(''); setHsayfa(1); }}>
                  <X size={14} />
                </button>
              )}
            </div>
            <div className={s.segs}>
              {HAREKET_TIPI.map((t) => (
                <button
                  key={t.id} type="button"
                  className={`${s.seg} ${htip === t.id ? s.segOn : ''}`}
                  onClick={() => { setHtip(t.id); setHsayfa(1); }}
                >{t.label}</button>
              ))}
            </div>
          </div>
          <div className={s.panelBody}>
            {hHata ? <div className={s.inlineErr}><AlertTriangle size={16} /> {hHata}</div>
              : hYuk && !hareket ? <SkelPanel rows={5} head={false} />
                : !hareket || hareket.items.length === 0 ? (
                  <div className={s.empty}><Inbox size={22} /><p className={s.emptyTitle}>Hareket yok</p></div>
                ) : (
                  <>
                    <div className={s.tableWrap}>
                      <table className={s.table}>
                        <thead>
                          <tr>
                            <th className={s.th}>Tarih</th>
                            <th className={s.th}>Fiş No</th>
                            <th className={s.th}>Açıklama</th>
                            <th className={`${s.th} ${s.num}`}>Borç</th>
                            <th className={`${s.th} ${s.num}`}>Alacak</th>
                            <th className={`${s.th} ${s.num}`}>Bakiye</th>
                          </tr>
                        </thead>
                        <tbody>
                          {hareket.items.map((h) => (
                            <tr key={h.id}>
                              <td className={`${s.td} ${s.muted}`}>{tarihTR(h.date)}</td>
                              <td className={`${s.td} ${s.doc}`}>{h.doc_no || '-'}</td>
                              <td className={s.td}><span className={s.desc} title={h.description || ''}>{h.description || '-'}</span></td>
                              <td className={`${s.td} ${s.num} ${h.debit ? s.debit : s.zero}`}>{h.debit ? formatTRY(h.debit) : '-'}</td>
                              <td className={`${s.td} ${s.num} ${h.credit ? s.credit : s.zero}`}>{h.credit ? formatTRY(h.credit) : '-'}</td>
                              <td className={`${s.td} ${s.num} ${s.strong}`}>{formatTRY(h.running)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>

                    <ul className={s.mList}>
                      {hareket.items.map((h) => (
                        <li key={`m-${h.id}`} className={s.mItem}>
                          <div className={s.mRow}>
                            <span className={s.mDesc}>{h.description || '-'}</span>
                            <span className={h.debit ? s.debit : s.credit}>
                              {h.debit ? <ArrowUpRight size={13} /> : <ArrowDownLeft size={13} />}
                              {formatTRY(h.debit || h.credit)}
                            </span>
                          </div>
                          <div className={s.mRow}>
                            <span className={s.muted}>{tarihTR(h.date)} · {h.doc_no || '-'}</span>
                            <span className={s.muted}>Bakiye {formatTRY(h.running)}</span>
                          </div>
                        </li>
                      ))}
                    </ul>

                    <div className={s.pager}>
                      <span className={s.pagerInfo}>
                        {formatNumber((hsayfa - 1) * 20 + 1)}–{formatNumber((hsayfa - 1) * 20 + hareket.items.length)} / {formatNumber(hareket.total)}
                      </span>
                      <div className={s.pagerBtns}>
                        <button
                          type="button" className={s.pageBtn} disabled={hsayfa <= 1 || hYuk}
                          onClick={() => setHsayfa((n) => Math.max(1, n - 1))}
                        ><ChevronLeft size={16} /></button>
                        <span className={s.pageNo}>{formatNumber(hsayfa)} / {formatNumber(hareket.total_pages)}</span>
                        <button
                          type="button" className={s.pageBtn}
                          disabled={hsayfa >= hareket.total_pages || hYuk}
                          onClick={() => setHsayfa((n) => n + 1)}
                        ><ChevronRight size={16} /></button>
                      </div>
                    </div>
                  </>
                )}
          </div>
        </section>
      )}

      {sekme === 'orders' && (
        <section className={s.panel}>
          <div className={s.panelHead}>
            <h3 className={s.panelTitle}><ShoppingCart size={17} /> Son Siparişler</h3>
          </div>
          <div className={s.panelBody}>
            {ilk ? <SkelPanel rows={4} head={false} />
              : siparisler.length === 0 ? (
                <div className={s.empty}><Inbox size={22} /><p className={s.emptyTitle}>Sipariş yok</p></div>
              ) : (
                <ul className={s.orderList}>
                  {siparisler.map((o) => {
                    const d = SIPARIS_DURUM[o.state] || SIPARIS_DURUM.processing;
                    return (
                      <li key={o.time} className={s.orderItem}>
                        <div className={s.orderMain}>
                          <span className={s.orderTime}>{zamanTR(o.time)}</span>
                          <span className={s.orderMeta}>
                            {formatNumber(o.lines)} kalem · {formatNumber(o.qty)} adet
                          </span>
                        </div>
                        <span className={`${s.status} ${s[d.cls]}`}>{d.label}</span>
                        <span className={s.orderAmount}>{formatTRY(o.amount)}</span>
                      </li>
                    );
                  })}
                </ul>
              )}
          </div>
        </section>
      )}

      {sekme === 'logins' && (
        <section className={s.panel}>
          <div className={s.panelHead}>
            <h3 className={s.panelTitle}><Globe size={17} /> Son Girişler</h3>
            <div className={s.panelMeta}>
              <span>Toplam <b>{formatNumber(data && data.login_count)}</b></span>
            </div>
          </div>
          <div className={s.panelBody}>
            {ilk ? <SkelPanel rows={4} head={false} />
              : girisler.length === 0 ? (
                <div className={s.empty}><Inbox size={22} /><p className={s.emptyTitle}>Giriş kaydı yok</p></div>
              ) : (
                <ul className={s.loginList}>
                  {girisler.map((g, i) => (
                    <li key={`${g.time}-${i}`} className={s.loginItem}>
                      <span className={s.loginAv}>{basHarfler(g.name)}</span>
                      <span className={s.loginBody}>
                        <span className={s.loginName}>{(g.name || '').trim() || '-'}</span>
                        <span className={s.loginSub}>{g.email || '-'}</span>
                      </span>
                      <span className={s.loginSide}>
                        <span className={s.loginTime}>{zamanTR(g.time)}</span>
                        {g.ip && <span className={s.loginIp}>{g.ip}</span>}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
          </div>
        </section>
      )}

      <UserFormModal
        acik={modalAcik} kod={kod} kullanici={duzenlenen}
        onKapat={() => { setModalAcik(false); setDuzenlenen(null); }}
        onKaydedildi={kaydedildi}
      />
      <ToastStack items={toastlar} onDismiss={dismiss} />
    </div>
  );
}
